import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { listChannels } from '../../api/channels';
import { listMembers } from '../../api/team';
import {
  FUNNEL_STAGE_LABELS,
  FUNNEL_STAGES,
  KOTBOT_BADGE_VALUES,
  kotbotBadgeLabel,
  kotbotBadgeTone,
  LOST_REASON_LABELS,
  type LostReason,
} from '../../api/types';
import { useAddNote, useContact, useContactSources, usePatchContact } from '../../hooks/use-contacts';
import { Avatar } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import { useToast } from '../ui/toast';

export function ContactDetailPanel({ contactId, onClose }: { contactId: string; onClose: () => void }) {
  const { data, isLoading } = useContact(contactId);
  const channels = useQuery({ queryKey: ['channels'], queryFn: listChannels });
  const members = useQuery({ queryKey: ['team', 'members'], queryFn: listMembers });
  const sources = useContactSources();
  const patch = usePatchContact();
  const addNoteMutation = useAddNote();
  const toast = useToast();
  const [noteText, setNoteText] = useState('');
  const [lostReason, setLostReason] = useState<LostReason>('other');

  if (isLoading) {
    return (
      <aside className="flex w-96 shrink-0 flex-col gap-3 border-l border-border p-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </aside>
    );
  }
  if (!data) return null;

  const { contact, consents, memory, recentEvents, activeDialogs } = data;
  const channelTitle = channels.data?.find((c) => c.id === contact.sourceChannelId)?.title;

  function changeStage(stage: string) {
    if (stage === 'lost') return; // handled by the reason select's own button below
    patch.mutate(
      { id: contactId, body: { funnel_stage: stage } },
      {
        onError: (err) => toast.push(err instanceof Error ? err.message : 'Не удалось сменить стадию'),
      },
    );
  }

  function markLost() {
    patch.mutate(
      { id: contactId, body: { funnel_stage: 'lost', lost_reason: lostReason } },
      {
        onError: (err) => toast.push(err instanceof Error ? err.message : 'Не удалось сменить стадию'),
      },
    );
  }

  function changeOwner(ownerId: string) {
    patch.mutate(
      { id: contactId, body: { owner_user_id: ownerId || null } },
      { onError: (err) => toast.push(err instanceof Error ? err.message : 'Не удалось назначить владельца') },
    );
  }

  function changeSource(sourceId: string) {
    patch.mutate(
      { id: contactId, body: { source_id: sourceId || null } },
      { onError: (err) => toast.push(err instanceof Error ? err.message : 'Не удалось изменить источник') },
    );
  }

  function changeKotbotBadge(value: string | null) {
    patch.mutate(
      { id: contactId, body: { kotbot_badge: value } },
      { onError: (err) => toast.push(err instanceof Error ? err.message : 'Не удалось изменить статус kotbot') },
    );
  }

  function submitNote(e: React.FormEvent) {
    e.preventDefault();
    if (!noteText.trim()) return;
    addNoteMutation.mutate(
      { id: contactId, text: noteText },
      {
        onSuccess: () => setNoteText(''),
        onError: (err) => toast.push(err instanceof Error ? err.message : 'Не удалось добавить заметку'),
      },
    );
  }

  return (
    <aside className="flex w-96 shrink-0 flex-col gap-4 overflow-y-auto border-l border-border p-4 text-sm">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-2">
          <Avatar name={[contact.firstName, contact.lastName].filter(Boolean).join(' ') || `VK ID ${contact.vkUserId}`} photoUrl={contact.photoUrl} size="md" />
          <div>
            <h2 className="font-semibold">
              {[contact.firstName, contact.lastName].filter(Boolean).join(' ') || `VK ID ${contact.vkUserId}`}
            </h2>
            <a
              className="text-xs text-primary hover:underline"
              href={`https://vk.com/id${contact.vkUserId}`}
              target="_blank"
              rel="noreferrer"
            >
              vk.com/id{contact.vkUserId}
            </a>
            {channelTitle && <div className="text-xs text-muted-fg">Канал: {channelTitle}</div>}
          </div>
        </div>
        <button onClick={onClose} className="text-muted-fg hover:text-fg" aria-label="Закрыть карточку">
          ✕
        </button>
      </div>

      <div>
        <div className="mb-1 text-xs font-medium text-muted-fg">Стадия воронки</div>
        <Select aria-label="Стадия воронки" value={contact.funnelStage} onChange={(e) => changeStage(e.target.value)}>
          {FUNNEL_STAGES.map((s) => (
            <option key={s} value={s}>
              {FUNNEL_STAGE_LABELS[s]}
            </option>
          ))}
        </Select>
        {contact.funnelStage === 'lost' && contact.lostReason && (
          <div className="mt-1 text-xs text-muted-fg">
            Причина: {LOST_REASON_LABELS[contact.lostReason as LostReason] ?? contact.lostReason}
          </div>
        )}
        <div className="mt-2 flex items-center gap-2">
          <Select aria-label="Причина потери" value={lostReason} onChange={(e) => setLostReason(e.target.value as LostReason)}>
            {(Object.keys(LOST_REASON_LABELS) as LostReason[]).map((r) => (
              <option key={r} value={r}>
                {LOST_REASON_LABELS[r]}
              </option>
            ))}
          </Select>
          <Button variant="danger" size="sm" onClick={markLost}>
            Пометить «Потерян»
          </Button>
        </div>
      </div>

      <div>
        <div className="mb-1 text-xs font-medium text-muted-fg">Владелец</div>
        <Select aria-label="Владелец" value={contact.ownerUserId ?? ''} onChange={(e) => changeOwner(e.target.value)}>
          <option value="">Без владельца</option>
          {(members.data?.members ?? []).map((m) => (
            <option key={m.userId} value={m.userId}>
              {m.name || m.email}
            </option>
          ))}
        </Select>
      </div>

      <div>
        <div className="mb-1 text-xs font-medium text-muted-fg">Источник</div>
        <Select aria-label="Источник" value={contact.sourceId ?? ''} onChange={(e) => changeSource(e.target.value)}>
          <option value="">Не указан</option>
          {(sources.data?.sources ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.title}
            </option>
          ))}
        </Select>
      </div>

      <div>
        <div className="mb-1 flex items-center gap-2 text-xs font-medium text-muted-fg">
          Kotbot <Badge tone={kotbotBadgeTone(contact.kotbotBadge)}>{kotbotBadgeLabel(contact.kotbotBadge)}</Badge>
        </div>
        {/* v1: ручная метка. Как только появится интеграция с API kotbot, её значение станет источником
            истины, а этот селект — резервным способом поправить статус вручную. */}
        <Select aria-label="Kotbot-статус" value={contact.kotbotBadge ?? ''} onChange={(e) => changeKotbotBadge(e.target.value || null)}>
          <option value="">—</option>
          {KOTBOT_BADGE_VALUES.map((v) => (
            <option key={v} value={v}>
              {kotbotBadgeLabel(v)}
            </option>
          ))}
        </Select>
      </div>

      <div>
        <div className="mb-1 text-xs font-medium text-muted-fg">Теги</div>
        {contact.tags.length === 0 ? (
          <span className="text-xs text-muted-fg">Нет тегов</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {contact.tags.map((t) => (
              <Badge key={t}>{t}</Badge>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="mb-1 text-xs font-medium text-muted-fg">Согласие на сообщения</div>
        {consents.length === 0 ? (
          <span className="text-xs text-muted-fg">Нет данных о согласии</span>
        ) : (
          <ul className="space-y-1">
            {consents.map((c) => (
              <li key={c.id} className="text-xs">
                <Badge tone={c.status === 'granted' ? 'success' : 'danger'}>{c.status}</Badge> {c.basis}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <div className="mb-1 text-xs font-medium text-muted-fg">Память: сводка</div>
        <p className="text-xs text-muted-fg">{memory?.summary ?? 'Нет сводки'}</p>
      </div>

      <div>
        <div className="mb-1 text-xs font-medium text-muted-fg">Активные диалоги</div>
        {activeDialogs.length === 0 ? (
          <span className="text-xs text-muted-fg">Нет активных диалогов</span>
        ) : (
          <ul className="space-y-1 text-xs">
            {activeDialogs.map((d) => (
              <li key={d.id}>
                {d.state} · SLA {d.slaStatus}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <div className="mb-1 text-xs font-medium text-muted-fg">Недавние события</div>
        {recentEvents.length === 0 ? (
          <span className="text-xs text-muted-fg">Нет событий</span>
        ) : (
          <ul className="space-y-1 text-xs">
            {recentEvents.map((e) => (
              <li key={e.id}>
                {e.type} · {new Date(e.at).toLocaleString('ru-RU')}
              </li>
            ))}
          </ul>
        )}
      </div>

      <form onSubmit={submitNote} className="space-y-2">
        <div className="text-xs font-medium text-muted-fg">Заметка</div>
        <textarea
          className="h-16 w-full rounded border border-border bg-card p-2 text-sm"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Добавить заметку…"
        />
        <Button type="submit" size="sm" disabled={!noteText.trim() || addNoteMutation.isPending}>
          Добавить
        </Button>
      </form>
    </aside>
  );
}
